import { useState, useEffect } from 'react';
import { Button, FormGroup, Label, Alert, Progress, Collapse } from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import TournamentDetailCard from './TournamentDetailCard';
import tournamentMigrationService, {
  TournamentForMigration,
  MigrationSummary,
} from '@/services/tournaments/tournament-migration.service';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import { TournamentStatus } from '@repo/common-types';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';

interface PlayerMigrationSectionProps {
  tournament: {
    id: string;
    name: string;
    contractAddress?: string | null;
  };
  onRefetch: () => void;
}

const PlayerMigrationSection = ({ tournament, onRefetch }: PlayerMigrationSectionProps) => {
  const [sourceTournament, setSourceTournament] = useState<TournamentForMigration | null>(null);
  const [destinationTournament, setDestinationTournament] = useState<TournamentForMigration | null>(null);
  const [availableTournaments, setAvailableTournaments] = useState<TournamentForMigration[]>([]);
  const [isLoadingTournaments, setIsLoadingTournaments] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [migrationResult, setMigrationResult] = useState<MigrationSummary | null>(null);
  const [migrationProgress, setMigrationProgress] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  // Load available tournaments
  useEffect(() => {
    loadAvailableTournaments();
  }, []);

  const loadAvailableTournaments = async () => {
    setIsLoadingTournaments(true);
    try {
      // Get all tournaments excluding the current one (since it's always the source)
      const tournaments = await tournamentMigrationService.getTournamentsForMigration(tournament.id);
      setAvailableTournaments(tournaments);

      // Fetch current tournament details separately to use as source
      const allTournaments = await tournamentMigrationService.getTournamentsForMigration();
      const currentTournamentOption = allTournaments.find((t) => t.id === tournament.id);
      if (currentTournamentOption) {
        setSourceTournament(currentTournamentOption);
      }
    } catch (error) {
      console.error('Failed to load tournaments:', error);
      toast.error('Failed to load available tournaments');
    } finally {
      setIsLoadingTournaments(false);
    }
  };

  const formatTournamentOption = (tournament: TournamentForMigration) => ({
    value: tournament.id,
    label: (
      <div className="d-flex flex-column">
        <span>{tournament.name}</span>
        <small className="text-muted">
          {tournament.registeredPlayers} players | Buy-in: ${tournament.buyIn} | Status: {tournament.status}
        </small>
      </div>
    ),
    data: tournament,
  });

  // Destination options are all available tournaments (already excludes current tournament)
  const destinationOptions = availableTournaments.map(formatTournamentOption);

  const handleMigrate = () => {
    if (!sourceTournament || !destinationTournament) {
      toast.error('Please select both source and destination tournaments');
      return;
    }

    if (!sourceTournament || sourceTournament.registeredPlayers === 0) {
      toast.error('Current tournament has no players to migrate');
      return;
    }

    if (destinationTournament.availableSlots < sourceTournament.registeredPlayers) {
      toast.error(
        `Destination tournament only has ${destinationTournament.availableSlots} available slots ` +
          `but needs ${sourceTournament.registeredPlayers} slots`,
      );
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmMigration = async () => {
    if (!sourceTournament || !destinationTournament) return;

    setShowConfirmModal(false);
    setIsMigrating(true);
    setMigrationProgress(0);
    setMigrationResult(null);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setMigrationProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 5;
        });
      }, 1000);

      const response = await tournamentMigrationService.migratePlayers(sourceTournament.id, destinationTournament.id);

      clearInterval(progressInterval);
      setMigrationProgress(100);

      if (response.success && response.summary) {
        setMigrationResult(response.summary);
        toast.success(`Migration completed! ${response.summary.successful} players migrated successfully`);

        // Reload tournaments to get updated counts
        await loadAvailableTournaments();
        onRefetch();
      } else {
        toast.error(response.error || 'Migration failed');
      }
    } catch (error: any) {
      console.error('Migration error:', error);
      toast.error(error?.response?.data?.error || 'Failed to migrate players');
    } finally {
      setIsMigrating(false);
    }
  };

  const getTournamentStatusColor = (status: string) => {
    switch (status) {
      case TournamentStatus.RUNNING:
        return 'success';
      case TournamentStatus.COMPLETED:
      case TournamentStatus.CANCELLED:
        return 'secondary';
      default:
        return 'primary';
    }
  };

  return (
    <div className="w-100 my-3">
      <TournamentDetailCard title="Player Migration" className="w-100">
        <div className="d-flex justify-content-end mb-2">
          <Button
            color="link"
            className="text-decoration-none d-flex align-items-center gap-1 px-0"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? <IoChevronDown size={18} /> : <IoChevronForward size={18} />}
            {isExpanded ? 'Hide migration tools' : 'Show migration tools'}
          </Button>
        </div>

        <Collapse isOpen={isExpanded}>
          <Alert color="warning" className="mb-3">
            <strong>⚠️ Admin Only</strong> - Migrate all players from this tournament to another tournament. Requires
            backend wallet to have sufficient USDT balance for registration fees (if not a free tournament).
          </Alert>

          <div className="row mb-4">
            <div className="col-md-12">
              <div className="mb-3">
                <strong>Source Tournament:</strong> {tournament.name}
                {sourceTournament && (
                  <small className="text-muted ms-2">({sourceTournament.registeredPlayers} players registered)</small>
                )}
              </div>
              <FormGroup>
                <Label for="destinationTournament">Select Destination Tournament</Label>
                <Select
                  id="destinationTournament"
                  options={destinationOptions}
                  value={destinationTournament ? formatTournamentOption(destinationTournament) : null}
                  onChange={(option) => setDestinationTournament(option?.data || null)}
                  isLoading={isLoadingTournaments}
                  isDisabled={isMigrating}
                  placeholder="Select destination tournament..."
                  formatOptionLabel={(option) => option.label}
                />
              </FormGroup>
            </div>
          </div>

          {/* Migration Summary */}
          {sourceTournament && destinationTournament && (
            <Alert color="info" className="mb-3">
              <strong>Migration Summary:</strong>
              <ul className="mb-0 mt-2">
                <li>Players in source tournament: {sourceTournament.registeredPlayers}</li>
                <li>Available slots in destination: {destinationTournament.availableSlots}</li>
                <li>Cost per player: ${destinationTournament.totalBuyin}</li>
                <li>
                  Maximum migration cost:{' '}
                  {destinationTournament.totalBuyin === 0
                    ? 'Free (no cost)'
                    : `$${destinationTournament.totalBuyin * sourceTournament.registeredPlayers}`}
                </li>
              </ul>
              <small className="text-muted">
                Note: The system will automatically detect and skip any players already registered in the destination
                tournament.
              </small>
            </Alert>
          )}

          <div className="d-flex justify-content-center mb-3">
            <Button
              color="primary"
              onClick={handleMigrate}
              disabled={!sourceTournament || !destinationTournament || isMigrating || !tournament?.contractAddress}
              className="px-4"
            >
              {isMigrating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Migrating...
                </>
              ) : (
                'Migrate Players'
              )}
            </Button>
          </div>

          {/* Progress Bar */}
          {isMigrating && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Migration Progress</small>
                <small className="text-muted">{Math.round(migrationProgress)}%</small>
              </div>
              <Progress value={migrationProgress} color="primary" />
              <small className="text-muted">Processing player registrations in batches...</small>
            </div>
          )}

          {/* Migration Results */}
          {migrationResult && !isMigrating && (
            <div className="mt-4">
              <Alert color={migrationResult.failed === 0 ? 'success' : 'warning'}>
                <strong>Migration Complete!</strong>
                <div className="mt-2">
                  {migrationResult.previouslyMigrated && migrationResult.previouslyMigrated > 0 && (
                    <div className="mb-2">
                      <small className="text-muted">
                        ℹ️ {migrationResult.previouslyMigrated} players were already migrated from previous attempts
                      </small>
                    </div>
                  )}
                  <div>✅ Total Successful: {migrationResult.successful} players</div>
                  {migrationResult.attemptedInThisRun !== undefined && migrationResult.attemptedInThisRun > 0 && (
                    <div className="text-muted">
                      <small>
                        ({migrationResult.attemptedInThisRun} attempted in this run,{' '}
                        {migrationResult.attemptedInThisRun - migrationResult.failed} succeeded)
                      </small>
                    </div>
                  )}
                  {migrationResult.failed > 0 && <div>❌ Failed: {migrationResult.failed} players</div>}
                  <div className="text-muted mt-1">
                    <small>
                      Duration:{' '}
                      {Math.round(
                        (new Date(migrationResult.completedAt).getTime() -
                          new Date(migrationResult.startedAt).getTime()) /
                          1000,
                      )}{' '}
                      seconds
                    </small>
                  </div>
                </div>
              </Alert>

              {/* Failed migrations details */}
              {migrationResult.failed > 0 && (
                <>
                  <Alert color="info" className="mb-2 mt-3">
                    <small>
                      💡 <strong>Tip:</strong> You can run the migration again to retry the failed players. The system
                      will automatically skip players who were already successfully migrated.
                    </small>
                  </Alert>
                  <details className="mt-2">
                    <summary className="text-muted" style={{ cursor: 'pointer' }}>
                      <small>View Failed Migrations ({migrationResult.failed})</small>
                    </summary>
                    <div className="mt-2">
                      {migrationResult.results
                        .filter((r) => !r.success)
                        .slice(0, 10)
                        .map((result, index) => (
                          <div key={index} className="text-danger">
                            <small>
                              • Player {result.playerId}: {result.error}
                            </small>
                          </div>
                        ))}
                      {migrationResult.results.filter((r) => !r.success).length > 10 && (
                        <div className="text-muted mt-1">
                          <small>... and {migrationResult.results.filter((r) => !r.success).length - 10} more</small>
                        </div>
                      )}
                    </div>
                  </details>
                </>
              )}
            </div>
          )}

          {!tournament?.contractAddress && (
            <div className="mt-2">
              <small className="text-muted">
                Player migration is only available for tournaments with a contract address.
              </small>
            </div>
          )}
        </Collapse>
      </TournamentDetailCard>

      {/* Confirmation Modal */}
      {showConfirmModal && sourceTournament && destinationTournament && (
        <ConfirmModal
          title="Confirm Player Migration"
          message="Are you sure you want to migrate players?"
          body={
            <>
              <ul>
                <li>
                  <strong>From:</strong> {tournament.name} (current tournament)
                </li>
                <li>
                  <strong>To:</strong> {destinationTournament.name}
                </li>
                <li>
                  <strong>Players:</strong> {sourceTournament.registeredPlayers}
                </li>
                <li>
                  <strong>Total Cost:</strong>{' '}
                  {destinationTournament.totalBuyin === 0
                    ? 'Free (no cost)'
                    : `$${destinationTournament.totalBuyin * sourceTournament.registeredPlayers}`}
                </li>
              </ul>
              <Alert color="warning" className="mb-0">
                This operation will register all players from the source tournament into the destination tournament
                {destinationTournament.totalBuyin > 0 && ' using the backend wallet funds'}. This cannot be undone.
              </Alert>
            </>
          }
          onConfirm={handleConfirmMigration}
          onClose={() => setShowConfirmModal(false)}
          confirmButtonText="Yes, Migrate Players"
          cancelButtonText="Cancel"
        />
      )}
    </div>
  );
};

export default PlayerMigrationSection;
